import { z } from 'zod/v4';

import {
    animationPluginShapeLegacy,
    extendedPositionPluginShapeLegacy,
    shadowSettingsSchemaLegacy,
    visibilityPluginShapeLegacy,
} from '../../../common/schemas/legacy';

/**
 * The figure shape variants, mirroring the literal union of
 * `ZeroFigureWidgetDataFormControls['figure']` in the host app.
 */
export const figureShapeSchema = z.enum([
    'round',
    'square',
    'triangle',
    'star',
    'rhombus',
    'burst',
    'diamond',
    'hexagon',
    'horizontal-line',
    'blob',
]);

/**
 * Data shape for the figure widget — **zero-block only** (the widget model
 * registers `params.type: ['zero']`; there is no default-column variant).
 *
 * - `backgroundColor`/`borderColor` hold a CSS colour string or a theme
 *   preset name — the host renders both via its colour pipeline.
 * - The border trio is `nullish()`: the host form types mark the controls
 *   optional (they only matter while `isBorder` is on, and only the
 *   `square`/`round` shapes expose them in the editor).
 * - `borderStyle` holds `ELineStyle` values; kept as `z.string()` so the
 *   package doesn't track the host enum.
 *
 * Mirrors the FormGroup produced by `getZeroFigureWidgetData` in the host app.
 */
export const figureLegacyZeroDataSchema = z.object({
    figure: figureShapeSchema,
    backgroundColor: z.string(),
    keepProportions: z.boolean(),
    isBorder: z.boolean(),
    borderWidth: z.number().nullish(),
    borderColor: z.string().nullish(),
    borderStyle: z.string().nullish(),
    // Bare required toggle — every persisted figure carries the flag, so it is
    // NOT the nullish `isShadow` from `shadowPluginShapeLegacy`.
    isShadow: z.boolean(),
    // Box-shadow settings for the `square`/`round` shapes (#71349). `nullish()`
    // because figures persisted before this feature carry no bag; `withDefaults`
    // backfills it. The clip-path shapes ignore it — they render a fixed
    // `drop-shadow` filter (which has no `spread`), so only the rectangular /
    // elliptical shapes expose the customizable box-shadow.
    shadowSettings: shadowSettingsSchemaLegacy.nullish(),
    ...animationPluginShapeLegacy,
    ...visibilityPluginShapeLegacy,
    ...extendedPositionPluginShapeLegacy,
});

export type FigureShape = z.infer<typeof figureShapeSchema>;
